import React, { Component } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import Recaptcha from 'react-recaptcha';
import * as Yup from 'yup';
import Button from '../button/Button';

import styles from './ContactsForm.module.scss';
import { RECAPTCHA_API_KEY } from '../../config';
import SuccessForm from './SuccessForm';
import Input from '../input/Input';
import Textarea from '../textarea/Textarea';
import Select from '../select/Select';
import withInputAnalytics from '../../enchancers/with-input-analytics';
import withSelectAnalytics from '../../enchancers/with-select-analytics';
import withClickAnalytics from '../../enchancers/with-click-analytics';

const API_ROOT = 'https://flatfy.com/api';

function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  const error = new Error(response.statusText);
  error.response = response;
  throw error;
}

function parseJSON(response) {
  return response.json();
}

const ContactsSchema = Yup.object().shape({
  email: Yup.string()
    .email()
    .required('We will contact you at this email address'),
  subject: Yup.string()
    .oneOf(['potential_partner', 'get_infographics', 'other'])
    .required('Choose which profession you belong to'),
  site: Yup.string().when('subject', {
    is: 'potential_partner',
    then: Yup.string().required('Site is required'),
    otherwise: Yup.string(),
  }),
  comment: Yup.string().required('Please, leave in comment'),
  recaptcha: Yup.string().required("Prove you're not a robot"),
});

const isValidUrl = (url) =>
  /^(http:\/\/|https:\/\/)?[a-zA-Z0-9-]+\.[a-zA-Z0-9\-.]+[a-z0-9]{2}$/i.test(url);

const TrackedInput = withInputAnalytics({ category: 'contact_form' })(Input);
const TrackedSelect = withSelectAnalytics({ category: 'contact_form' })(Select);
const TrackedTextarea = withInputAnalytics({ category: 'contact_form' })(Textarea);
const TrackedButton = withClickAnalytics({
  category: 'contact_form',
  defaultAction: 'send_btn_click',
})(Button);

class ContactsForm extends Component {
  state = {
    isSuccess: false,
    isError: false,
  };

  handleSubmit = (values) => {
    fetch(`${API_ROOT}/feedback`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        purpose: 'infographics',
        subject: values.subject,
        site: values.site,
        text: values.comment,
        email: values.email,
        'g-recaptcha-response': values.recaptcha,
      }),
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(() => {
        this.setState({
          isSuccess: true,
        });
      })
      .catch((e) => {
        this.setState({
          isSuccess: true,
        });
        console.error(e);
      });
  };

  render() {
    const { isSuccess, isError } = this.state;
    return (
      <div>
        {isSuccess ? (
          <SuccessForm />
        ) : (
          <Formik
            initialValues={{
              email: '',
              subject: '',
              site: '',
              comment: '',
              recaptcha: '',
            }}
            validationSchema={ContactsSchema}
            onSubmit={this.handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            render={({ values, touched, errors, isValidating, handleBlur, handleChange }) => (
              <Form className={styles.root} noValidate>
                <div className={styles.row}>
                  <div className={styles.inlineGroup}>
                    <div className={styles.inlineText}>
                      <span className={styles.caption}>My email</span>
                    </div>
                    <div className={styles.inlineInput}>
                      <label className={styles.hiddenLabel} htmlFor="email">
                        E-mail
                      </label>
                      <TrackedInput
                        startEventAction="email_start"
                        editEventAction="email_edit"
                        finishEventAction="email_finish"
                        name="email"
                        id="email"
                        type="email"
                        placeholder="name@company.com"
                        value={values.email}
                        error={touched.email && errors.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>

                  <ErrorMessage component="div" className={styles.errorMessage} name="email" />
                </div>

                <div className={styles.row}>
                  <div className={styles.inlineGroup}>
                    <div className={styles.inlineText}>
                      <span className={styles.caption}>I’d like</span>
                    </div>
                    <div className={styles.inlineInput}>
                      <label className={styles.hiddenLabel} htmlFor="subject">
                        Subject
                      </label>
                      <TrackedSelect
                        changeEventAction="i_want_select"
                        name="subject"
                        id="subject"
                        required
                        value={values.subject}
                        error={touched.subject && errors.subject}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <option value="" hidden disabled>
                          Select an option
                        </option>
                        <option value="potential_partner">to be a partner</option>
                        <option value="get_infographics">to get the infographics</option>
                        <option value="other">other</option>
                      </TrackedSelect>
                    </div>
                  </div>

                  <ErrorMessage component="div" className={styles.errorMessage} name="subject" />
                </div>

                {values.subject === 'potential_partner' && (
                  <div className={styles.row}>
                    <div className={styles.inlineGroup}>
                      <div className={styles.inlineText}>
                        <span className={styles.caption}>Here’s my site</span>
                      </div>
                      <div className={styles.inlineInput}>
                        <label className={styles.hiddenLabel} htmlFor="website">
                          Website
                        </label>
                        <TrackedInput
                          startEventAction="site_start"
                          editEventAction="site_edit"
                          finishEventAction="site_finish"
                          trackValue
                          name="site"
                          id="site"
                          type="text"
                          placeholder="www.mysite.com"
                          value={values.site}
                          error={touched.site && errors.site}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                    {touched.site && !isValidUrl(values.site) && (
                      <div className={styles.hint}>
                        You need to have your ads published on a website if you want to become our
                        partner.{' '}
                        <a
                          href="https://flatfy.in/partnership#become-partner"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          About partnership
                        </a>
                      </div>
                    )}
                    <ErrorMessage component="div" className={styles.errorMessage} name="site" />
                  </div>
                )}

                <div className={styles.row}>
                  <span className={styles.caption}>More specifically,</span>
                  <label className={styles.hiddenLabel} htmlFor="comment">
                    Comment
                  </label>
                  <TrackedTextarea
                    startEventAction="comment_start"
                    editEventAction="comment_edit"
                    finishEventAction="comment_finish"
                    name="comment"
                    id="comment"
                    placeholder="Comment"
                    value={values.comment}
                    error={touched.textarea && errors.comment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <ErrorMessage component="div" className={styles.errorMessage} name="comment" />
                </div>

                <div className={styles.row}>
                  <div className={styles.recaptchaContainer}>
                    <Field name="recaptcha">
                      {({ field, form }) => (
                        <Recaptcha
                          sitekey={RECAPTCHA_API_KEY}
                          render="explicit"
                          onloadCallback={() => {}}
                          verifyCallback={(value) => form.setFieldValue(field.name, value)}
                        />
                      )}
                    </Field>
                  </div>

                  <ErrorMessage component="div" className={styles.errorMessage} name="recaptcha" />
                </div>

                {isError && (
                  <div className={styles.row}>
                    <p className={styles.textError}>Something went wrong. Try again later</p>
                  </div>
                )}

                <div className={styles.actionRow}>
                  <TrackedButton type="submit" disabled={isValidating}>
                    Send
                  </TrackedButton>
                </div>
              </Form>
            )}
          />
        )}
      </div>
    );
  }
}

export default ContactsForm;
